import React, { useState, useEffect, useRef } from 'react';
import {
  FaFilePdf,
  FaCheckCircle,
  FaExclamationTriangle,
  FaEye,
  FaPlay,
  FaRedo,
  FaArrowLeft,
  FaSpinner,
  FaExclamationCircle,
  FaTimesCircle
} from 'react-icons/fa';
import { checkJobStatus } from '../components/UploadContent/utils/uploadContentUtils';

const ProgressScreen = ({ 
  uploadedFiles, 
  onViewData, 
  onReturnToUpload,
  extractionStartTime,
  initialExtractions = null,
  jobId: propJobId
}) => {
  const [extractions, setExtractions] = useState([]);
  const [isExtracting, setIsExtracting] = useState(true); // Start as true for backend
  const [completedCount, setCompletedCount] = useState(0);
  const [jobStatus, setJobStatus] = useState(null);
  const [pollingInterval, setPollingInterval] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const extractionIntervals = useRef({});
  const hasStartedRef = useRef(false);

  // Get job ID from uploaded files or prop
  const jobId = propJobId || uploadedFiles[0]?.jobId;

  // Initialize extraction progress
  useEffect(() => {
    console.log('ProgressScreen useEffect triggered:', { 
      uploadedFiles, 
      jobId, 
      hasJobId: !!jobId,
      fileCount: uploadedFiles.length
    });
    
    // Log detailed file info for debugging
    uploadedFiles.forEach((file, index) => {
      console.log(`File ${index}:`, {
        name: file.name,
        jobId: file.jobId,
        id: file.id,
        hasJobId: !!file.jobId
      });
    });

    if (uploadedFiles.length > 0 && jobId) {
      console.log('Starting progress tracking for job:', jobId);
      
      if (initialExtractions && initialExtractions.length > 0) {
        // If we have initial extractions (coming back from data view), use them
        setExtractions(initialExtractions);
        
        // Calculate completed count from initial extractions
        const completed = initialExtractions.filter(e => e.status === 'completed' || e.status === 'skipped').length;
        setCompletedCount(completed);
        
        // Check if any extractions can view data
        const canViewAnyData = initialExtractions.some(e => e.canViewData);
        if (canViewAnyData) {
          setIsExtracting(false);
        } else {
          setIsExtracting(true);
        }
        
        hasStartedRef.current = true;
      } else if (extractions.length === 0) {
        // Initial load - start fresh
        const initialExtractions = uploadedFiles.map((file, index) => ({
          id: file.id,
          name: file.name,
          progress: 0,
          status: 'pending',
          estimatedTime: Math.floor(Math.random() * 20) + 15,
          jobId: file.jobId || jobId, // Use file.jobId if available, otherwise use global jobId
          statementId: null,
          statementType: null,
          excelPath: null,
          canViewData: false,
          companyName: file.companyName || 'Unknown'
        }));
        setExtractions(initialExtractions);
        
        // Start polling job status
        startJobPolling();
      }
    } else if (!jobId) {
      console.error('No job ID found in uploaded files:', uploadedFiles);
      console.warn('No job ID found for progress tracking. Files:', uploadedFiles);
      setErrorMessage('No job ID found. Please restart the upload process.');
      setIsExtracting(false);
    }
  }, [uploadedFiles, jobId, initialExtractions]);

  
  // Start polling job status from backend
  const startJobPolling = () => {
    if (!jobId) {
      console.error('Cannot start polling without job ID');
      setErrorMessage('Job ID is required to track progress');
      return;
    }

    console.log('Starting job polling for job ID:', jobId);
    setIsExtracting(true);
    setErrorMessage(null);

    // Clear any existing interval
    if (pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }

    const interval = setInterval(async () => {
      try {
        console.log(`Polling job ${jobId}...`);
        const result = await checkJobStatus(jobId);
        
        console.log('Job status result:', result);
        
        if (result.success) {
          const statusData = result.data;
          console.log('Job status update received:', {
            status: statusData.status,
            progress: statusData.progress_percent,
            statements: statusData.statements?.length || 0,
            excel_files: statusData.excel_files?.length || 0
          });
          
          setJobStatus(statusData);
          setErrorMessage(null);
          
          // Update extraction progress based on backend status
          updateExtractionsFromBackend(statusData);
          
          // Check if job is complete
          if (statusData.status === 'completed' || statusData.status === 'failed' || statusData.status === 'partially_completed') {
            console.log('Job completed with status:', statusData.status);
            clearInterval(interval);
            setPollingInterval(null);
            setIsExtracting(false);
            
            // Update counts
            if (statusData.statements) {
              const completed = statusData.statements.filter(s => 
                s.status === 'completed' || s.status === 'skipped'
              ).length;
              setCompletedCount(completed);
            }
            
            // If completed successfully, show success message
            if (statusData.status === 'completed') {
              console.log('Job completed successfully! Excel files:', statusData.excel_files);
            }
          }
        } else {
          console.error('Job status check failed:', result.error);
          setErrorMessage(result.error || 'Failed to check job status');
        }
      } catch (error) {
        console.error('Error in job polling:', error);
        setErrorMessage(`Polling error: ${error.message}`);
      }
    }, 3000); // Poll every 3 seconds

    setPollingInterval(interval);
    
    // Set timeout to stop polling after 10 minutes
    setTimeout(() => {
      if (interval) {
        console.log('Polling timeout reached after 10 minutes');
        clearInterval(interval);
        setPollingInterval(null);
        setIsExtracting(false);
        setErrorMessage('Job polling timeout. Please check the job manually.');
      }
    }, 10 * 60 * 1000); // 10 minutes
  };

  // Update extractions based on backend status
  const updateExtractionsFromBackend = (statusData) => {
    console.log('Updating extractions from backend:', {
      statusData: statusData,
      statementsCount: statusData.statements?.length || 0,
      excelFiles: statusData.excel_files?.length || 0
    });

    if (!statusData.statements || statusData.statements.length === 0) {
      console.log('No statements in status data');
      return;
    }

    // Group statements by original filename (PDF name)
    const statementsByPdf = {};
    statusData.statements.forEach(statement => {
      const pdfName = statement.original_filename || 'Unknown PDF';
      if (!statementsByPdf[pdfName]) {
        statementsByPdf[pdfName] = [];
      }
      statementsByPdf[pdfName].push(statement);
    });

    console.log('Statements grouped by PDF:', Object.keys(statementsByPdf));

    // Update extractions state
    setExtractions(prev => {
      const updatedExtractions = prev.map(extraction => {
        // Find matching statements for this PDF - use filename matching
        const matchingStatements = Object.entries(statementsByPdf)
          .find(([pdfName]) => {
            // Check if extraction name matches PDF name (case-insensitive, allow partial matches)
            const extractionNameLower = extraction.name.toLowerCase();
            const pdfNameLower = pdfName.toLowerCase();
            
            // Try exact match first
            if (extractionNameLower === pdfNameLower) return true;
            
            // Try contains match (PDF might have path)
            if (pdfNameLower.includes(extractionNameLower)) return true;
            
            // Try the other way around
            if (extractionNameLower.includes(pdfNameLower)) return true;
            
            return false;
          });
        
        const pdfStatements = matchingStatements ? matchingStatements[1] : [];
        
        console.log(`File ${extraction.name}: found ${pdfStatements.length} statements`);
        
        if (pdfStatements.length > 0) {
          // Calculate average progress for this PDF
          let avgProgress = pdfStatements.length > 0 
            ? Math.round(pdfStatements.reduce((sum, s) => sum + (s.progress || 0), 0) / pdfStatements.length)
            : 0;
          
          // Determine overall status
          let overallStatus = 'pending';
          let canViewData = false;
          let statementId = null;
          let statementType = null;
          let excelPath = null;
          
          // Check if all statements for this PDF are completed
          const allCompleted = pdfStatements.every(s => 
            s.status === 'completed' || s.status === 'skipped'
          );
          
          const hasFailed = pdfStatements.some(s => s.status === 'failed');
          const isProcessing = pdfStatements.some(s => s.status === 'processing');
          
          if (allCompleted) {
            overallStatus = 'completed';
            avgProgress = 100; // Force 100% when completed
            
            // Can view data if at least one statement has an Excel path
            const completedStatements = pdfStatements.filter(s => 
              s.status === 'completed' && s.excel_path
            );
            
            if (completedStatements.length > 0) {
              canViewData = true;
              // Use first completed statement's data for viewing
              statementId = completedStatements[0]?.id;
              statementType = completedStatements[0]?.statement_type;
              excelPath = completedStatements[0]?.excel_path;
            } else if (statusData.excel_files && statusData.excel_files.length > 0) {
              // If no statement has excel_path but job has Excel files, we can still view data
              canViewData = true;
              statementId = pdfStatements[0]?.id;
              statementType = pdfStatements[0]?.statement_type;
              excelPath = statusData.excel_files[0]?.download_url; // Use first Excel file
            }
          } else if (hasFailed) {
            overallStatus = 'error';
          } else if (isProcessing) {
            overallStatus = 'extracting';
          } else if (pdfStatements.some(s => s.status === 'pending')) {
            overallStatus = 'pending';
          }
          
          // If job is completed overall but this PDF doesn't have statements, mark as completed
          if (statusData.status === 'completed' && pdfStatements.length === 0) {
            overallStatus = 'completed';
            avgProgress = 100;
            // Try to get Excel file from job
            if (statusData.excel_files && statusData.excel_files.length > 0) {
              canViewData = true;
              excelPath = statusData.excel_files[0]?.download_url;
            }
          }
          
          return {
            ...extraction,
            progress: avgProgress,
            status: overallStatus,
            statementId: statementId,
            statementType: statementType,
            excelPath: excelPath,
            canViewData: canViewData,
            errorMessage: pdfStatements.find(s => s.status === 'failed')?.error_message
          };
        }
        
        // If no statements found but job is completed, mark as completed
        if (statusData.status === 'completed' || statusData.status === 'partially_completed') {
          console.log(`File ${extraction.name}: No statements but job completed, marking as completed`);
          return {
            ...extraction,
            progress: 100,
            status: 'completed',
            canViewData: statusData.excel_files && statusData.excel_files.length > 0
          };
        }
        
        return extraction;
      });
      
      console.log('Updated extractions:', updatedExtractions);
      return updatedExtractions;
    });

    // Update completed count
    const completedStatements = statusData.statements.filter(s => 
      s.status === 'completed' || s.status === 'skipped'
    ).length;
    setCompletedCount(completedStatements);
  };

  // Map backend status to frontend status
  const mapBackendStatus = (backendStatus) => {
    switch(backendStatus) {
      case 'completed':
      case 'skipped':
        return 'completed';
      case 'failed':
        return 'error';
      case 'processing':
        return 'extracting';
      case 'pending':
        return 'pending';
      default:
        return 'pending';
    }
  };

  // Clean up intervals on unmount
  useEffect(() => {
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
      Object.values(extractionIntervals.current).forEach(interval => {
        clearInterval(interval);
      });
    };
  }, [pollingInterval]);

  // Helper functions
  const determineStatementTypeFromExcel = (filename) => {
    if (!filename) return 'income_statement';
    
    if (filename.includes('income')) return 'income_statement';
    if (filename.includes('balance')) return 'balance_sheet';
    if (filename.includes('cash')) return 'cash_flow';
    
    return 'income_statement';
  };

  const determineStatementTypeFromFilename = (filename) => {
    if (!filename) return 'income_statement';
    
    // Simple mapping based on filename patterns
    const lowerName = filename.toLowerCase();
    if (lowerName.includes('income') || lowerName.includes('p&l') || lowerName.includes('profit')) {
      return 'income_statement';
    }
    if (lowerName.includes('balance') || lowerName.includes('financial position')) {
      return 'balance_sheet';
    }
    if (lowerName.includes('cash') || lowerName.includes('flow')) {
      return 'cash_flow';
    }
    
    return 'income_statement';
  };

  const handleViewData = (fileId) => {
    const extraction = extractions.find(f => f.id === fileId);
    const uploadedFile = uploadedFiles.find(f => f.id === fileId);
    
    if (!extraction || !uploadedFile) {
      console.error('File not found:', fileId);
      return;
    }

    console.log('Attempting to view data:', {
      extraction: extraction,
      canViewData: extraction.canViewData,
      status: extraction.status,
      excelPath: extraction.excelPath,
      jobStatus: jobStatus
    });

    // Check if we can view data
    if ((extraction.canViewData || extraction.status === 'completed') && onViewData) {
      // If no statementId but job has Excel files, use first Excel file
      let statementId = extraction.statementId;
      let statementType = extraction.statementType;
      let excelPath = extraction.excelPath;
      
      if (!statementId && jobStatus?.excel_files && jobStatus.excel_files.length > 0) {
        // Find the Excel file that matches the statement type
        const matchingExcel = jobStatus.excel_files.find(excel => 
          determineStatementTypeFromExcel(excel.filename) === extraction.statementType
        );
        
        if (matchingExcel) {
          statementId = matchingExcel.id;
          excelPath = matchingExcel.download_url;
        } else {
          // Fallback to first Excel file
          statementId = jobStatus.excel_files[0]?.id;
          excelPath = jobStatus.excel_files[0]?.download_url;
        }
      }
      
      if (!excelPath && jobStatus?.excel_files && jobStatus.excel_files.length > 0) {
        excelPath = jobStatus.excel_files[0]?.download_url;
      }
      
      if (!statementType) {
        statementType = determineStatementTypeFromFilename(uploadedFile.name);
      }
      
      // Create data object with backend information
      const viewData = {
        ...uploadedFile,
        status: extraction.status,
        progress: extraction.progress,
        extractions: extractions,
        jobId: jobId,
        statementId: statementId,
        statementType: statementType,
        excelPath: excelPath,
        companyName: jobStatus?.company_name || uploadedFile.companyName || extraction.companyName,
        tickerId: jobStatus?.company_id ? `ID-${jobStatus.company_id}` : 'N/A',
        isBackendData: true,
        periodType: 'annual',
        canViewData: true
      };
      
      console.log('Viewing backend data:', viewData);
      onViewData(viewData);
    } else if (!extraction.canViewData) {
      if (jobStatus?.excel_files && jobStatus.excel_files.length > 0) {
        // Job has Excel files but this extraction doesn't know about them
        console.log('Excel files are available. Trying to load data...');
        
        // Force view with first Excel file
        const viewData = {
          ...uploadedFile,
          status: 'completed',
          progress: 100,
          extractions: extractions,
          jobId: jobId,
          statementId: jobStatus.excel_files[0]?.id,
          statementType: determineStatementTypeFromExcel(jobStatus.excel_files[0]?.filename),
          excelPath: jobStatus.excel_files[0]?.download_url,
          companyName: jobStatus?.company_name || uploadedFile.companyName,
          tickerId: jobStatus?.company_id ? `ID-${jobStatus.company_id}` : 'N/A',
          isBackendData: true,
          periodType: 'annual',
          canViewData: true
        };
        
        console.log('Forcing view with Excel files:', viewData);
        onViewData(viewData);
      } else {
        alert('Data extraction is complete but no Excel file was found. Please check backend logs.');
      }
    } else {
      alert('Cannot view data yet. Please wait for extraction to complete.');
    }
  };

  // Restart extraction (for frontend simulation only)
  const restartExtraction = () => {
    if (pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }
    
    // Reset states
    setExtractions(prev => prev.map(item => ({
      ...item,
      progress: 0,
      status: 'pending',
      statementId: null,
      excelPath: null,
      canViewData: false,
      errorMessage: null
    })));
    
    setCompletedCount(0);
    setJobStatus(null);
    setErrorMessage(null);
    setIsExtracting(true);
    hasStartedRef.current = false;
    
    // Start polling again
    setTimeout(() => {
      startJobPolling();
    }, 1000);
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'completed': return 'bg-green-100 text-green-800 border-green-300 dark:bg-green-900/30 dark:text-green-300 dark:border-green-700';
      case 'extracting': return 'bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-700';
      case 'error': return 'bg-red-100 text-red-800 border-red-300 dark:bg-red-900/30 dark:text-red-300 dark:border-red-700';
      default: return 'bg-gray-100 text-gray-800 border-gray-300 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700';
    }
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'completed': return <FaCheckCircle className="text-green-500" />;
      case 'extracting': return <FaSpinner className="animate-spin text-blue-500" />;
      case 'error': return <FaExclamationTriangle className="text-red-500" />;
      default: return <div className="text-gray-400">●</div>;
    }
  };

  const getStatusText = (status) => {
    switch(status) {
      case 'completed': return 'Completed';
      case 'extracting': return 'Extracting';
      case 'error': return 'Error';
      default: return 'Pending';
    }
  };

  // Calculate remaining time
  const calculateRemainingTime = () => {
    if (!jobStatus || !isExtracting) return 'Unknown';
    
    const activeExtractions = extractions.filter(e => e.status === 'extracting' || e.status === 'pending');
    if (activeExtractions.length === 0) return '0s';
    
    // Estimate based on average progress
    const totalProgress = activeExtractions.reduce((sum, item) => sum + item.progress, 0);
    const avgProgress = totalProgress / activeExtractions.length;
    
    if (avgProgress === 0) return '2-3 min';
    if (avgProgress < 30) return '1-2 min';
    if (avgProgress < 70) return '30-60s';
    return '< 30s';
  };

  // Manual start button
  const handleManualStart = () => {
    if (!isExtracting && extractions.length > 0) {
      startJobPolling();
    }
  };

  // Render backend status overview
  const renderBackendStatus = () => {
    if (!jobStatus && !errorMessage) {
      return (
        <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
          <div className="flex items-center justify-center gap-3">
            <FaSpinner className="animate-spin text-blue-500" />
            <span className="text-blue-700 dark:text-blue-300">Connecting to backend server...</span>
          </div>
        </div>
      );
    }

    if (errorMessage) {
      return (
        <div className="mt-4 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
          <div className="flex items-start gap-3">
            <FaExclamationCircle className="text-red-500 mt-0.5" />
            <div className="flex-1">
              <p className="text-red-700 dark:text-red-300 font-medium mb-1">
                Error: {typeof errorMessage === 'object' ? JSON.stringify(errorMessage) : errorMessage}
              </p>
              <p className="text-sm text-red-600 dark:text-red-400">
                Job ID: {jobId || 'Not available'}
              </p>
              <div className="mt-2">
                <button
                  onClick={restartExtraction}
                  className="px-3 py-1.5 text-xs bg-red-100 hover:bg-red-200 text-red-700 dark:bg-red-900/30 dark:hover:bg-red-900/50 dark:text-red-300 rounded transition-colors"
                >
                  Retry Polling
                </button>
                <button
                  onClick={() => setErrorMessage(null)}
                  className="ml-2 px-3 py-1.5 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-300 rounded transition-colors"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Check if job failed
    if (jobStatus.status === 'failed') {
      return (
        <div className="mt-4 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
          <div className="flex items-center gap-3">
            <FaExclamationCircle className="text-red-500" />
            <div>
              <p className="text-red-700 dark:text-red-300 font-medium">Job Failed</p>
              <p className="text-sm text-red-600 dark:text-red-400 mt-1">
                {jobStatus.error_message || 'Unknown error occurred'}
              </p>
              <p className="text-xs text-red-500 dark:text-red-400 mt-2">
                Note: This file may have been processed before. Try uploading a different file or contact support.
              </p>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900 dark:text-white">
              {jobStatus.progress_percent || 0}%
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">Overall Progress</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600 dark:text-green-400">
              {jobStatus.completed_statements || 0}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">Completed</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
              {jobStatus.processing_statements || 0}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">Processing</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-red-600 dark:text-red-400">
              {jobStatus.failed_statements || 0}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">Failed</div>
          </div>
        </div>
        
        {/* Job Info */}
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-gray-500 dark:text-gray-400">Job ID:</span>{' '}
            <span className="font-mono text-gray-900 dark:text-gray-300">{jobId}</span>
          </div>
          <div>
            <span className="text-gray-500 dark:text-gray-400">Company:</span>{' '}
            <span className="font-medium text-gray-900 dark:text-gray-300">
              {jobStatus.company_name || 'Unknown'}
            </span>
          </div>
          <div>
            <span className="text-gray-500 dark:text-gray-400">Status:</span>{' '}
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
              jobStatus.status === 'completed' ? 'bg-green-100 text-green-800' :
              jobStatus.status === 'failed' ? 'bg-red-100 text-red-800' :
              jobStatus.status === 'partially_completed' ? 'bg-yellow-100 text-yellow-800' :
              'bg-blue-100 text-blue-800'
            }`}>
              {jobStatus.status?.toUpperCase() || 'PROCESSING'}
            </span>
          </div>
          <div>
            <span className="text-gray-500 dark:text-gray-400">Estimated Time:</span>{' '}
            <span className="text-gray-900 dark:text-gray-300">
              {calculateRemainingTime()}
            </span>
          </div>
        </div>
        
        {/* Excel Files Info */}
        {jobStatus.excel_files && jobStatus.excel_files.length > 0 && (
          <div className="mt-3 p-3 bg-green-50 dark:bg-green-900/20 rounded-md">
            <div className="text-sm text-green-700 dark:text-green-300">
              <strong>Excel Files Generated:</strong> {jobStatus.excel_files.length}
              <ul className="mt-1 space-y-1">
                {jobStatus.excel_files.map((file, idx) => (
                  <li key={idx} className="text-xs">
                    • {file.filename} ({file.file_size_mb || '0'}MB)
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
        
        {jobStatus.error_message && (
          <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/20 rounded-md">
            <div className="text-sm text-red-700 dark:text-red-300">
              <strong>Error:</strong> {jobStatus.error_message}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <button
            onClick={onReturnToUpload}
            className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-300 
                     hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
            title="Back to Upload"
          >
            <FaArrowLeft className="text-xl" />
          </button>
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Data Extraction Progress
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {jobId ? `Job ID: ${jobId}` : 'Waiting for job information...'}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          {isExtracting ? (
            <div className="flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-800 rounded-full">
              <FaSpinner className="animate-spin" />
              <span className="text-sm font-medium">Processing</span>
            </div>
          ) : completedCount === uploadedFiles.length ? (
            <div className="flex items-center gap-2 px-4 py-2 bg-green-100 text-green-800 rounded-full">
              <FaCheckCircle />
              <span className="text-sm font-medium">Completed</span>
            </div>
          ) : (
            <div className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-800 rounded-full">
              <FaTimesCircle />
              <span className="text-sm font-medium">Stopped</span>
            </div>
          )}
        </div>
      </div>

     

     

      {/* Progress Table */}
      <div className="mt-6 bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden border border-gray-200 dark:border-gray-700">
        {extractions.length === 0 ? (
          <div className="p-8 text-center">
            <FaFilePdf className="text-4xl text-gray-300 dark:text-gray-600 mx-auto mb-3" />
            <p className="text-gray-500 dark:text-gray-400">No files to process</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      S.No
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      PDF Files
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Progress Level
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Statement Type
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {extractions.map((item, index) => {
                    const isCompleted = item.status === 'completed';
                    const canViewData = item.canViewData; // Use the explicit flag
                    
                    console.log(`File ${item.name}: canViewData=${canViewData}, status=${item.status}, statementId=${item.statementId}, excelPath=${item.excelPath}`);
                    
                    return (
                      <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {index + 1}
                          </div>
                        </td>
                        
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10 bg-red-50 dark:bg-red-900/20 rounded-lg flex items-center justify-center mr-3">
                              <FaFilePdf className="text-red-500 text-lg" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 dark:text-white truncate max-w-xs">
                                {item.name}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                Size: {item.size || 'Unknown'} MB
                              </p>
                              {item.errorMessage && (
                                <p className="text-xs text-red-500 dark:text-red-400 mt-1 truncate">
                                  Error: {item.errorMessage}
                                </p>
                              )}
                            </div>
                          </div>
                        </td>
                        
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="w-48">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                {item.progress}%
                              </span>
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                {item.progress === 100 ? 'Completed' : 
                                 isExtracting ? 'Processing' : 'Pending'}
                              </span>
                            </div>
                            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
                              <div 
                                className={`h-2.5 rounded-full transition-all duration-300 ${
                                  item.progress === 100 ? 'bg-green-500' :
                                  item.progress >= 70 ? 'bg-blue-500' :
                                  item.progress >= 40 ? 'bg-yellow-500' :
                                  'bg-red-500'
                                }`}
                                style={{ width: `${item.progress}%` }}
                              />
                            </div>
                          </div>
                        </td>
                        
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium border ${getStatusColor(item.status)}`}>
                            {getStatusIcon(item.status)}
                            <span>{getStatusText(item.status)}</span>
                          </div>
                        </td>
                        
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-700 dark:text-gray-300">
                            {item.statementType ? 
                              item.statementType.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) : 
                              'Not specified'}
                          </div>
                        </td>
                        
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => {
                                console.log('View Data clicked:', {
                                  fileId: item.id,
                                  canViewData: canViewData,
                                  status: item.status,
                                  progress: item.progress,
                                  statementId: item.statementId,
                                  excelPath: item.excelPath
                                });
                                handleViewData(item.id);
                              }}
                              disabled={!canViewData}
                              className={`px-4 py-2 text-xs font-medium rounded-lg transition-colors flex items-center gap-2 ${
                                canViewData
                                  ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-sm'
                                  : 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                              }`}
                              title={canViewData ? "View extracted data" : "Wait for extraction to complete"}
                            >
                              <FaEye className="text-xs" />
                              View Data
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 dark:bg-gray-900 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {isExtracting ? (
                    <span className="flex items-center gap-2">
                      <FaSpinner className="animate-spin" />
                      Backend processing in progress... {calculateRemainingTime()} remaining
                    </span>
                  ) : completedCount === uploadedFiles.length ? (
                    <span className="flex items-center gap-2 text-green-600 dark:text-green-400">
                      <FaCheckCircle /> All files extracted successfully
                    </span>
                  ) : (
                    `Extraction ${completedCount > 0 ? 'stopped' : 'ready to start'}. ${completedCount} of ${uploadedFiles.length} files completed`
                  )}
                </div>
                <div className="flex items-center gap-4">
                  {!isExtracting && completedCount < uploadedFiles.length && (
                    <button
                      onClick={restartExtraction}
                      className="flex items-center gap-2 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <FaRedo />
                      Retry Extraction
                    </button>
                  )}
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <div className="h-3 w-3 bg-red-500 rounded"></div>
                      <span className="text-xs text-gray-600 dark:text-gray-400">0-39%</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="h-3 w-3 bg-yellow-500 rounded"></div>
                      <span className="text-xs text-gray-600 dark:text-gray-400">40-69%</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="h-3 w-3 bg-blue-500 rounded"></div>
                      <span className="text-xs text-gray-600 dark:text-gray-400">70-99%</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="h-3 w-3 bg-green-500 rounded"></div>
                      <span className="text-xs text-gray-600 dark:text-gray-400">100%</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
      
     
    </div>
  );
};

export default ProgressScreen;